import { Injectable, inject } from "@angular/core";
import { catchError, map, switchMap, takeWhile, tap, withLatestFrom } from "rxjs";

import { Actions, createEffect, ofType } from "@ngrx/effects";
// Store
import { Store } from "@ngrx/store";
import * as SportsbookActions from "../../../stores/sportsbook/sportsbook.actions";
import { SportsbookFacade } from "../../../stores/sportsbook/sportsbook.facade";
import { SportsbookState } from "../../../stores/sportsbook/sportsbook.reducers";

// Services
import { DeviceService } from "@services/device.service";
import { BufferFacade } from "@shared-providers/facades";

import { SportTrackPollingService } from "@sports-services/index";
import { SPORTS_NAVIGATION_SERVICE_ERROR_CODES } from "@sports-services/sports.navigation.service";
import { SportService } from "@sports-services/sports.service";

// Models
import * as models from "@sports-models/index";

import { SPORTS_PATHS } from "@shared-constants/routes";
// Utils
import { SportBookService } from "@sports-services/index";
import { SPORTS_NAVIGATION_SERVICE_ERROR_LOCATION } from "@sports-services/sports.navigation.service/sports.navigations.constants";

@Injectable({ providedIn: "root" })
export class SportsbookHomeEffects {
  // Services
  actions$ = inject(Actions);
  bufferFacade = inject(BufferFacade);
  sportService = inject(SportService);

  // Utils
  sportBookService = inject(SportBookService);
  deviceService = inject(DeviceService);
  store = inject(Store<SportsbookState>);
  sportsbookFacade = inject(SportsbookFacade);
  sportTrackPollingService = inject(SportTrackPollingService);

  isDesktop: boolean;
  isMobile: boolean;

  intervalTimeout = null;
  intervalCounter = null;

  timer = {
    live: "live",
    regular: "regular",
    highlights: "highlights",
  };

  constructor() {
    this.isDesktop = this.deviceService.isDesktop();
    this.isMobile = this.deviceService.isMobile();
  }

  // HOME
  initHome$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.initHome),
      map(() => SportsbookActions.setHomeHighlighsSelectedSport({ sport: "soccer" })),
    ),
  );

  setHomeHighlighsSelectedSport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.setHomeHighlighsSelectedSport),
      map((selectedSport) => {
        if (this.intervalTimeout) {
          clearInterval(this.intervalTimeout);
        }
        return SportsbookActions.fetchHome(selectedSport);
      }),
    ),
  );

  fetchHome$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.fetchHome),
      withLatestFrom(this.store.select("sportsbook")),
      switchMap(([selectedSport, store]) => {
        this.sportTrackPollingService.startHomePagePolling();
        const gameTypes: models.C_GameTypes = new models.C_GameTypes(store.config.gameTypes);
        const params = {
          countHomeLiveEvents: 5,
          gameTypesHomeLiveEvents: gameTypes.serialize(gameTypes.getAll()),
          sportHandle: selectedSport.sport,
          countHighlightsEvents: 5,
          gameTypesHighlightsEvents: gameTypes.serialize(gameTypes.getSport(selectedSport.sport)),
        };

        return this.bufferFacade
          .newAuxBuffer(
            this.sportService,
            "getHomeData",
            params,
            this.timer.regular,
            this.sportTrackPollingService.homePolling$,
          )
          .pipe(
            takeWhile((_) => this.sportTrackPollingService.homePolling$.getValue()),
            map(([prevHomeData, currentHomeData]) => {
              const updatedCurrentHomeData = this.sportBookService.setHomeData(
                prevHomeData,
                currentHomeData,
                this.isMobile,
                selectedSport.sport,
              );
              return SportsbookActions.setHome(updatedCurrentHomeData);
            }),
            catchError((e) => {
              this.intervalCounter = 5;
              this.sportsbookFacade.restartingHomePollingTick(this.intervalCounter);
              console.log("Restarting Home polling in 5 seconds...");
              this.sportsbookFacade.setSportsError({
                errorCode: SPORTS_NAVIGATION_SERVICE_ERROR_CODES.GENERIC_POLLING_ERROR,
                errorDescription: SPORTS_NAVIGATION_SERVICE_ERROR_LOCATION.LOCATION_6,
              });
              this.intervalTimeout = setInterval(() => {
                if (this.intervalCounter === 0) {
                  this.sportsbookFacade.setHomeHighlighsSelectedSport(selectedSport.sport);
                  clearInterval(this.intervalTimeout);
                } else {
                  this.intervalCounter--;
                  this.sportsbookFacade.restartingHomePollingTick(this.intervalCounter);
                }
              }, 1000);
              return [];
            }),
          );
      }),
    ),
  );

  cancelPollingRestarting$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SportsbookActions.navigationUpdate),
        withLatestFrom(this.store.select("sportsbook")),
        tap(([{ type, ...payload }, data]) => {
          const targetUrl = payload.prevUrl.url?.split("?")[0].slice(1);
          const currentUrl = payload.currentUrl.url?.split("?")[0].slice(1);
          if (targetUrl !== currentUrl)
            switch (targetUrl) {
              case SPORTS_PATHS.HomePage:
                if (this.intervalTimeout) {
                  clearInterval(this.intervalTimeout);
                }
                break;
            }
        }),
      ),
    { functional: true, dispatch: false },
  );
}
